/**
 * Reservation Statistics 2 — extracción y agregación (§2.2, §8).
 *
 * El reporte lista, por **rate code**, un bloque de filas (No. of Rooms, % Occ.,
 * ADR, Room Revenue, …) con una columna por día. Nos interesa **solo** la fila
 * `No. of Rooms`: es la noche vendida bajo ese código.
 *
 * **Diamond no es el reporte: es un rate code.** Es la forma de reservar y
 * cobrar en USD de la cadena, y cada hotel lo escribe distinto — `DIAM`,
 * `DIAM$`, `DIAM $`, `DIAMD` (con desayuno), `DIAMADRES` (promo). Por eso la
 * regla es por PREFIJO sobre el código normalizado (mayúsculas, sin espacios ni
 * `$`): todo lo que empiece por `DIAM` suma. Así un código nuevo entra solo, sin
 * mantener listas por hotel.
 *
 * Dos formatos conviven, y NO son el mismo dato:
 *
 * | Formato   | Encabezado                        | Qué trae                        |
 * | --------- | --------------------------------- | ------------------------------- |
 * | `semanal` | `Monday 27-07-26  Tuesday …`      | 1 columna por día (3 a 7 días)  |
 * | `mtd`     | `DAY   MONTH   YEAR`              | **solo** el día del filtro      |
 *
 * En el formato `mtd` se toma ÚNICAMENTE la columna DAY: MONTH y YEAR son
 * acumulados y meterlos como si fueran un día es exactamente el error que
 * `validateDiamondDay` existe para bloquear.
 *
 * Gemini SOLO lee y transcribe; la suma de Diamond la hace `diamondFromResStat`.
 */
import { z } from 'zod';
import { isValidIso } from './dates.js';
import { SEDE_CODES, type SedeCodigo } from './types.js';

const cell = z.number().int().nullable();

export const resStatRateCodeSchema = z.object({
  /** Código tal cual aparece en el PDF (con `$` y espacios): es la auditoría. */
  code: z.string(),
  /** Fila "No. of Rooms" alineada a `fechas`, un valor por día. */
  rooms: z.array(cell).default([]),
});

export const resStatSchema = z.object({
  sede_codigo: z.enum(SEDE_CODES).nullable(),
  /** Variante de layout detectada por el modelo. */
  formato: z.enum(['semanal', 'mtd']).nullable().optional(),
  /** Fechas de las columnas de día (YYYY-MM-DD). En `mtd`, exactamente una. */
  fechas: z.array(z.string()).default([]),
  rate_codes: z.array(resStatRateCodeSchema).default([]),
  /** Fila "Grand Total" → "No. of Rooms" por día (contexto, no es Diamond). */
  grand_total_rooms: z.array(cell).default([]),
});

export type ResStat = z.infer<typeof resStatSchema>;
export type ResStatFormato = 'semanal' | 'mtd';

/**
 * Normaliza un rate code para compararlo: mayúsculas y sin nada que no sea
 * A-Z0-9. `"DIAM $"`, `"DIAM$"` y `"diam"` colapsan al mismo `DIAM`.
 */
export function normalizeRateCode(code: string): string {
  return code.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/** ¿Este rate code es una venta Diamond? Regla única: prefijo `DIAM`. */
export function isDiamondCode(code: string): boolean {
  return normalizeRateCode(code).startsWith('DIAM');
}

export interface RateCodeRooms {
  code: string;
  rooms: number;
}

export interface ResStatDay {
  fecha: string;
  /** Σ "No. of Rooms" de los rate codes Diamond de ese día. */
  diamond: number;
  /** Los códigos que sumaron (para que la revisión muestre de dónde sale). */
  codigosDiamond: RateCodeRooms[];
  /** Fila Grand Total del reporte ese día (todas las ventas), o null. */
  grandTotal: number | null;
  /** Todos los rate codes del día — se guardan para auditoría y uso futuro. */
  rateCodes: RateCodeRooms[];
}

export interface ResStatResult {
  sedeCodigo: SedeCodigo | null;
  formato: ResStatFormato;
  days: ResStatDay[];
  /** Avisos no bloqueantes, en español. */
  issues: string[];
}

function n(v: number | null | undefined): number {
  return v == null || !Number.isFinite(v) ? 0 : v;
}

/**
 * Reencaja una extracción con la forma VIEJA del reporte Diamond
 * (`week_dates` / `markets`) en el contrato nuevo. Existe para que las ingestas
 * ya extraídas antes de este cambio sigan abriéndose en la revisión en vez de
 * aparecer vacías. Idempotente sobre la forma nueva.
 */
export function normalizeResStatShape(obj: unknown): unknown {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj;
  const o = obj as Record<string, unknown>;
  if (o.fechas || o.rate_codes) return o; // ya es la forma nueva
  if (!o.week_dates && !o.markets) return o; // no es la vieja; que falle zod
  return {
    sede_codigo: o.sede_codigo ?? null,
    formato: 'semanal',
    fechas: o.week_dates ?? [],
    rate_codes: o.markets ?? [],
    grand_total_rooms: o.grand_total_rooms ?? [],
  };
}

/**
 * Convierte la extracción en noches Diamond por día. `diamond` sale SIEMPRE de
 * sumar los rate codes Diamond del día: nunca del Grand Total (que son todas las
 * ventas del hotel, Diamond incluido).
 */
export function diamondFromResStat(g: ResStat): ResStatResult {
  const issues: string[] = [];
  const formato: ResStatFormato = g.formato === 'mtd' ? 'mtd' : 'semanal';
  const days: ResStatDay[] = [];

  if (!g.sede_codigo) issues.push('No se detectó la sede del reporte.');
  if (g.fechas.length === 0) issues.push('No se detectaron fechas de columnas.');
  if (!g.rate_codes.some((r) => isDiamondCode(r.code))) {
    issues.push(
      'El reporte no trae ningún rate code Diamond (DIAM*): las noches Diamond quedarán en 0.',
    );
  }

  g.fechas.forEach((fecha, i) => {
    if (!isValidIso(fecha)) {
      issues.push(`Fecha inválida en la columna ${i + 1}: ${fecha}.`);
      return;
    }
    const rateCodes: RateCodeRooms[] = g.rate_codes.map((r) => ({
      code: r.code.trim(),
      rooms: n(r.rooms[i]),
    }));
    const codigosDiamond = rateCodes.filter((r) => isDiamondCode(r.code));
    const diamond = codigosDiamond.reduce((s, r) => s + r.rooms, 0);
    const gt = g.grand_total_rooms[i];
    const grandTotal = gt != null && Number.isFinite(gt) ? gt : null;

    // Diamond es un subconjunto de lo vendido: superarlo delata columnas
    // desalineadas o un acumulado leído como día. Avisa, no bloquea.
    if (grandTotal != null && diamond > grandTotal) {
      issues.push(`(${fecha}) Diamond (${diamond}) supera el Grand Total del día (${grandTotal}).`);
    }
    days.push({ fecha, diamond, codigosDiamond, grandTotal, rateCodes });
  });

  // Un mismo día repetido en dos columnas casi siempre es una lectura torcida.
  const vistas = new Set<string>();
  for (const d of days) {
    if (vistas.has(d.fecha)) issues.push(`Fecha repetida en el reporte: ${d.fecha}.`);
    vistas.add(d.fecha);
  }

  return { sedeCodigo: g.sede_codigo, formato, days, issues };
}

/** Prompt de extracción del reporte "Reservation Statistics 2". */
export const GEMINI_RESSTAT_PROMPT = `Eres un extractor de datos del reporte hotelero "Reservation Statistics" (estadísticas de reservas por Rate Code).
Devuelve EXCLUSIVAMENTE un objeto JSON válido (sin markdown ni texto adicional) con este contrato.

Reglas:
1. Identifica la SEDE por el encabezado del reporte. Devuelve uno de: ${SEDE_CODES.join(', ')} en "sede_codigo" (o null si no es claro).
   - "Lidotel Hotel Boutique Margarita" => "MGTA"; "... Barquisimeto" => "BQTO"; "... Valencia" => "VAL"; "... San Cristobal" => "SC"; "Hotel Centro Lido" => "CCS"; "Agua Dorada" => "AD".
   - Si el encabezado dice solo "Lidotel Hotel Boutique" SIN ciudad, es Paraguaná => "PAR".
2. El reporte viene en DOS formatos. Detecta cuál es y ponlo en "formato":
   - "semanal": la fila de encabezado trae días de la semana (Monday, Tuesday, …) y bajo cada uno una fecha DD-MM-YY. Puede traer de 3 a 7 columnas de día. Devuelve TODAS sus fechas en "fechas", en el mismo orden de izquierda a derecha.
   - "mtd": la fila de encabezado trae "DAY", "MONTH", "YEAR" en vez de días. En ese caso usa SOLO la columna DAY y devuelve UNA sola fecha en "fechas": la del pie "Filter Calendar/Month to Date DD-MM-YY". NUNCA uses MONTH ni YEAR: son acumulados, no días.
3. Las fechas están en formato DD-MM-YY (ej. "27-07-26" = 27 de julio de 2026). Conviértelas a "YYYY-MM-DD" con el año del encabezado ("Rate Code - 2026" => 2026).
4. Para CADA rate code del reporte (DIAM $, RACK, COR2, HUSE, NRAT, WEB1 $, VIPD, …) extrae ÚNICAMENTE la fila "No. of Rooms" como un arreglo de enteros alineado a "fechas" (un valor por día), en "rate_codes": [{ "code": "DIAM $", "rooms": [2, 5, 8] }, …].
   - Copia el código TAL CUAL aparece, con sus espacios y su "$" si los tiene.
   - NO extraigas % Occ., ADR, Room Revenue, F&B Revenue, Misc Revenue, No. of Guest, % Multi Occ., Single Occ. ni Multi Occ.
5. Extrae también la fila "Grand Total" → "No. of Rooms" por día en "grand_total_rooms" (alineado a "fechas").
6. El reporte tiene VARIAS PÁGINAS: las columnas de día se repiten en cada página y los rate codes continúan. Recorre TODAS las páginas e incluye todos los rate codes que veas, sin repetir ninguno.
7. Alinea fila por fila: localiza la etiqueta a la izquierda y desplázate por SU MISMA fila hasta cada columna. No mezcles valores de filas vecinas.
8. Si una celda está vacía o es 0, usa 0. No estimes ni calcules totales: solo transcribe lo que ves.`;
